import React, { useState } from "react";
import "./Service.css";
import {
  FaClipboardList,
  FaCogs,
  FaDesktop,
  FaStar,
  FaRegListAlt,
  FaRegPlayCircle,
  FaChevronLeft,
  FaChevronRight,
} from "react-icons/fa";

export default function Service() {
  const services = [
    {
      title: "Web Development",
      description:
        "Build scalable, responsive, and visually stunning websites tailored to your business needs.",
      icon: "🌐",
    },
    {
      title: "Mobile App Development",
      description:
        "Create user-friendly and feature-rich mobile applications for iOS and Android platforms.",
      icon: "📱",
    },
    {
      title: "Digital Marketing",
      description:
        "Boost your brand visibility with SEO, social media campaigns, ads management, and performance-driven marketing strategies.",
      icon: "📈",
    },

    {
      title: "Cloud Solutions",
      description:
        "Leverage cloud technologies to ensure seamless scalability and secure storage.",
      icon: "☁️",
    },
    {
      title: "Software Testing",
      description:
        "Ensure the quality and performance of your software with our comprehensive testing services.",
      icon: "🧪",
    },
    {
      title: "Custom Software Development",
      description:
        "Develop tailored software solutions to streamline and optimize your business processes.",
      icon: "💻",
    },
  ];

  const testimonials = [
    {
      text: "Their approach to project management and execution is commendable. They go above and beyond to ensure project success while maintaining complete transparency.",
      author: "– Child Safety",
    },
    {
      text: "We are really impressed by the quality of services we received. Very professional on cloud and application development, and courteous in dealings. Looking forward to a long partnership!",
      author: "– JusstMingle",
    },
    {
      text: "The team is technically sound and extremely quality conscious. They respond quickly and provide creative solutions even to complex challenges.",
      author: "– ProrA.Info",
    },
  ];

  const faqs = [
    {
      question: "How do you usually start a new project?",
      answer:
        "We begin with a discovery workshop to understand your goals, current systems, and constraints. From there we propose a phased roadmap with clear timelines and deliverables.",
    },
    {
      question: "Can you work with our existing tech stack?",
      answer:
        "Yes. We frequently integrate with and modernize existing systems instead of replacing everything. We adopt the best-fit tools while respecting your current investments.",
    },
    {
      question: "Do you offer ongoing support and maintenance?",
      answer:
        "Absolutely. We provide flexible support models, including dedicated teams, retainer-based support, and SLA-driven maintenance contracts.",
    },
    {
      question: "What engagement models do you offer?",
      answer:
        "We support fixed-scope projects, time & material for evolving requirements, and dedicated teams for long-running products.",
    },
  ];

  const [currentIndex, setCurrentIndex] = useState(0);
  const [openFaqIndex, setOpenFaqIndex] = useState(0);

  const handlePrev = () => {
    setCurrentIndex((prevIndex) =>
      prevIndex === 0 ? testimonials.length - 1 : prevIndex - 1
    );
  };

  const handleNext = () => {
    setCurrentIndex((prevIndex) =>
      prevIndex === testimonials.length - 1 ? 0 : prevIndex + 1
    );
  };

  const toggleFaq = (index) => {
    setOpenFaqIndex((prev) => (prev === index ? -1 : index));
  };

  return (
    <section className="services-section">
      {/* CORE SERVICES */}
      <div className="container">
        <h2 className="section-title">Our Core Services</h2>
        <p className="services-subtext">
          We offer a wide range of services to help your organisation build
          reliable, modern, and scalable software.
        </p>

        <div className="services-grid">
          {services.map((service, index) => (
            <div key={index} className="service-card">
              <div className="icon">{service.icon}</div>
              <h3 className="service-title">{service.title}</h3>
              <p className="service-description">{service.description}</p>
            </div>
          ))}
        </div>
      </div>

      {/* ENGAGEMENT MODELS */}
      <section className="engagement-section">
        <div className="container">
          <h2 className="section-title">Engagement Models</h2>
          <p className="services-subtext">
            Choose the way you want to work with us based on your product stage,
            budget, and timelines.
          </p>

          <div className="engagement-grid">
            <div className="engagement-card">
              <h3>Fixed Scope</h3>
              <p>
                Ideal when your requirements are clear and well-defined. We
                agree on scope, timelines, and cost up front.
              </p>
              <ul>
                <li>Clear milestones</li>
                <li>Predictable budget</li>
                <li>Best for MVPs & reworks</li>
              </ul>
            </div>

            <div className="engagement-card engagement-card-featured">
              <h3>Dedicated Team</h3>
              <p>
                A cross-functional team that works as an extension of your
                in-house engineering organisation.
              </p>
              <ul>
                <li>Continuous delivery</li>
                <li>High collaboration</li>
                <li>Perfect for growing products</li>
              </ul>
            </div>

            <div className="engagement-card">
              <h3>Time & Material</h3>
              <p>
                Great for evolving requirements where we iterate and prioritise
                together based on business impact.
              </p>
              <ul>
                <li>Flexible scope</li>
                <li>Pay-as-you-go</li>
                <li>Best for discovery & R&amp;D</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* HOW WE BUILD SOFTWARE */}
      <div className="services-containers">
        <h2 className="services-title">
          How We Turn Your Ideas into Impactful Software
        </h2>
        <div className="services-grid process-grid">
          <div className="service-card">
            <FaClipboardList className="service-icon" />
            <h3>Define scope & feasibility</h3>
            <p>
              Understand your objectives, constraints, and success metrics
              before writing a single line of code.
            </p>
          </div>
          <div className="service-card">
            <FaCogs className="service-icon" />
            <h3>Shape detailed specifications</h3>
            <p>
              Translate your ideas into user stories, flows, and technical
              requirements that everyone understands.
            </p>
          </div>
          <div className="service-card">
            <FaDesktop className="service-icon" />
            <h3>Design intuitive experiences</h3>
            <p>
              Create user journeys, prototypes, and UI systems that are both
              beautiful and practical.
            </p>
          </div>
          <div className="service-card">
            <FaStar className="service-icon" />
            <h3>Build with modern tech</h3>
            <p>
              Implement secure, scalable, and maintainable solutions using
              proven tools and best practices.
            </p>
          </div>
          <div className="service-card">
            <FaRegListAlt className="service-icon" />
            <h3>Test & deploy with confidence</h3>
            <p>
              Automated and manual testing, performance checks, and smooth
              rollout strategies.
            </p>
          </div>
          <div className="service-card">
            <FaRegPlayCircle className="service-icon" />
            <h3>Support & iterate</h3>
            <p>
              Continuous monitoring, enhancements, and optimisation based on
              real user feedback.
            </p>
          </div>
        </div>
      </div>

      {/* FAQ SECTION (INTERACTIVE) */}
      <section className="faq-section">
        <div className="container">
          <h2 className="section-title">Frequently Asked Questions</h2>
          <p className="services-subtext">
            A few quick answers to common questions about how we work.
          </p>

          <div className="faq-list">
            {faqs.map((item, index) => (
              <div
                key={index}
                className={`faq-item ${
                  openFaqIndex === index ? "faq-item-open" : ""
                }`}
              >
                <button
                  className="faq-question"
                  onClick={() => toggleFaq(index)}
                >
                  <span>{item.question}</span>
                  <span className="faq-toggle">
                    {openFaqIndex === index ? "−" : "+"}
                  </span>
                </button>
                {openFaqIndex === index && (
                  <p className="faq-answer">{item.answer}</p>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="testimonial-section">
        <div className="testimonial-container">
          {/* Left Section */}
          <div className="stable-section">
            <h1>Thousands of stories of growth</h1>
            <p>Find out what our clients have to say about us.</p>
            <div className="testimonial-badges">
              <span>Enterprise Apps</span>
              <span>Product Startups</span>
              <span>Global Clients</span>
            </div>
          </div>

          {/* Right Section */}
          <div className="changing-section">
            <div className="testimonial-card">
              <div className="quote-icon">“</div>
              <p>{testimonials[currentIndex].text}</p>
              <h4>{testimonials[currentIndex].author}</h4>
            </div>
            <div className="testimonial-navigation">
              <button className="nav-button" onClick={handlePrev}>
                <FaChevronLeft />
              </button>
              <button className="nav-button" onClick={handleNext}>
                <FaChevronRight />
              </button>
            </div>
          </div>
        </div>
      </section>
    </section>
  );
}
